import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Avatar,
  Badge,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  SmartToy as BotIcon,
  Add as AddIcon,
  MenuBook as BookIcon,
  Delete as DeleteIcon,
  WarningAmber as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as ClockIcon,
  Error as AlertCircleIcon
} from "@mui/icons-material";

const AssistantHeader = ({ 
  status = "available", 
  onNewChat, 
  onClearConversation, 
  onToggleSources,
  showSourcesButton = true 
}) => {
  const theme = useTheme();
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const statusConfig = {
    available: {
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      label: "Disponible",
      dotColor: theme.palette.success.main
    },
    responding: {
      icon: <ClockIcon fontSize="small" />,
      color: 'info',
      label: "En cours de réponse",
      dotColor: theme.palette.info.main
    },
    unavailable: {
      icon: <AlertCircleIcon fontSize="small" />,
      color: 'error',
      label: "Indisponible",
      dotColor: theme.palette.error.main
    }
  };

  const currentStatus = statusConfig[status] || statusConfig.available;

  const handleClear = () => {
    onClearConversation();
    setShowClearConfirm(false);
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        color="inherit" 
        elevation={0}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', zIndex: 10 }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 3 }, py: 1 }}>
          
          {/* Left: Avatar and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: currentStatus.dotColor,
                  color: currentStatus.dotColor,
                  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                  '&::after': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    animation: status === 'responding' ? 'ripple 1.2s infinite ease-in-out' : 'none',
                    border: '1px solid currentColor',
                    content: '""',
                  },
                },
                '@keyframes ripple': {
                  '0%': { transform: 'scale(.8)', opacity: 1 },
                  '100%': { transform: 'scale(2.4)', opacity: 0 },
                },
              }}
            >
              <Avatar 
                sx={{ 
                  width: 48, 
                  height: 48, 
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: theme.shadows[2]
                }}
              >
                <BotIcon />
              </Avatar>
            </Badge>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ lineHeight: 1.2 }}>
                Assistant IA RH
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Smart HR Management Platform
              </Typography>
            </Box>
          </Box>

          {/* Center: Status (Desktop only) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <Chip 
              icon={currentStatus.icon} 
              label={currentStatus.label} 
              color={currentStatus.color}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 500, borderRadius: 2 }}
            />
          </Box>

          {/* Right: Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showSourcesButton && (
              <Tooltip title="Afficher les sources">
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<BookIcon />}
                  onClick={onToggleSources}
                  sx={{ display: { xs: 'none', sm: 'flex' }, borderRadius: 2, textTransform: 'none', color: 'text.secondary' }}
                >
                  Sources
                </Button>
              </Tooltip>
            )}
            {showSourcesButton && (
              <Tooltip title="Afficher les sources">
                <IconButton onClick={onToggleSources} sx={{ display: { xs: 'flex', sm: 'none' }, color: 'text.secondary' }}>
                  <BookIcon />
                </IconButton>
              </Tooltip>
            )}
            
            <Tooltip title="Nouveau chat">
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onNewChat}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: theme.shadows[2],
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                }}
              >
                Nouveau chat
              </Button>
            </Tooltip>
            <Tooltip title="Nouveau chat">
              <IconButton 
                onClick={onNewChat}
                color="primary"
                sx={{ display: { xs: 'flex', sm: 'none' } }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Effacer la conversation">
              <IconButton 
                onClick={() => setShowClearConfirm(true)}
                color="error"
                sx={{ ml: 1, '&:hover': { bgcolor: 'error.light', color: 'white' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Clear Confirmation Dialog */}
      <Dialog open={showClearConfirm} onClose={() => setShowClearConfirm(false)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          Effacer la conversation ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action effacera tous les messages de la conversation actuelle. Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearConfirm(false)} color="inherit" sx={{ borderRadius: 2 }}>
            Annuler
          </Button>
          <Button onClick={handleClear} color="error" variant="contained" sx={{ borderRadius: 2 }}>
            Effacer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssistantHeader;
